import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  Save,
  Plus,
  Trash2,
  IndianRupee,
  HardHat,
  ArrowLeft,
  Package,
  Wallet,
  Minus,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  FileUp,
  Eye,
  FileText,
} from "lucide-react";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function SiteDetailedPage() {
  const { id } = useParams();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );
 
  // State for Site and Supervisors
  const [site, setSite] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [summary, setSummary] = useState({ totalDays: 0, totalSpend: 0 });
  const [openingBalance, setOpeningBalance] = useState(0);
  const [dailyOutflow, setDailyOutflow] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [addCash, setAddCash] = useState("");
  const [cashIn, setCashIn] = useState(0);
  const [saving, setSaving] = useState(false);
  const [mbEntries, setMbEntries] = useState([
    { title: "", amount: "", file: null, url: "" },
  ]);

  const [invoiceEntries, setInvoiceEntries] = useState([
    { title: "", file: null, url: "" },
  ]);

  const [mbUrl, setMbUrl] = useState("");
  const [invoiceUrl, setInvoiceUrl] = useState("");


  // --- New State for Report Modal ---
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportConfig, setReportConfig] = useState({
    type: 'single', // 'single', 'range', 'full'
    startDate: selectedDate,
    endDate: selectedDate
  });

  // Helper to fetch data for the report
  async function getReportData() {
    let query = supabase.from("daily_logs").select("*").eq("site_id", id);

    if (reportConfig.type === 'single') {
      query = query.eq("date", reportConfig.startDate);
    } else if (reportConfig.type === 'range') {
      query = query.gte("date", reportConfig.startDate).lte("date", reportConfig.endDate);
    }
    // 'full' doesn't need additional filters

    const { data, error } = await query.order("date", { ascending: true });
    if (error) {
      toast.error("Failed to fetch report data");
      return null;
    }
    return data;
  }

  // 🔁 Get previous day closing balance
  const prevDate = new Date(selectedDate);
  prevDate.setDate(prevDate.getDate() - 1);
  const prevDateStr = prevDate.toISOString().split("T")[0];

  async function getPreviousClosingBalance(date) {
    const prev = new Date(date);
    prev.setDate(prev.getDate() - 1);
    const prevDateStr = prev.toISOString().split("T")[0];

    const { data } = await supabase
      .from("daily_logs")
      .select("closing_balance")
      .eq("site_id", id)
      .eq("date", prevDateStr)
      .maybeSingle();

    return data?.closing_balance || 0;
  }

  const { data: prevLog } = supabase
    .from("daily_logs")
    .select("closing_balance")
    .eq("site_id", id)
    .eq("date", prevDateStr)
    .maybeSingle();

  // Form State
  const [logData, setLogData] = useState({
    supervisor_id: "",
    labour_details: [
      { type: "Skilled", count: 0 },
    ],
    materials: [], // { name: '', qty: '', unit: 'Bags', cost: 0 }
    expenses: [], // { item: '', amount: '' }
    opening_balance: 0,
    closing_balance: 0,
  });

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  useEffect(() => {
    fetchDailyLog();
  }, [id, selectedDate]);

  useEffect(() => {
    const materialTotal = logData.materials.reduce(
      (s, m) => s + Number(m.cost || 0),
      0,
    );

    const expenseTotal = logData.expenses.reduce(
      (s, e) => s + Number(e.amount || 0),
      0,
    );

    const total = materialTotal + expenseTotal;

    setDailyOutflow(total);

    const closing = Number(logData.opening_balance || 0) - total;
    setClosingBalance(closing);
  }, [logData]);

  // --- Date Navigation ---
  const handleDateStep = (step) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + step);
    setSelectedDate(d.toISOString().split("T")[0]);
  };

  async function fetchInitialData() {
    try {
      const { data: siteData } = await supabase
        .from("sites")
        .select("*")
        .eq("id", id)
        .single();
      const { data: supData } = await supabase.from("supervisors").select("*");

      // Calculate Site Summary
      const { data: allLogs } = await supabase
        .from("daily_logs")
        .select("total_expenses")
        .eq("site_id", id);
      const totalSpend =
        allLogs?.reduce(
          (sum, log) => sum + Number(log.total_expenses || 0),
          0,
        ) || 0;

      setSite(siteData);
      setSupervisors(supData || []);
      setMbUrl(siteData?.mb_url || "");
      setInvoiceUrl(siteData?.invoice_url || "");
      setSummary({ totalDays: allLogs?.length || 0, totalSpend });
      setMbEntries(siteData?.mb_files || []);
      setInvoiceEntries(siteData?.invoice_files || []);
    } catch (err) {
      toast.error("Error loading site information");
    }
  }

  async function fetchDailyLog() {
    setLoading(true);

    const prevClosing = await getPreviousClosingBalance(selectedDate);

    const { data } = await supabase
      .from("daily_logs")
      .select("*")
      .eq("site_id", id)
      .eq("date", selectedDate)
      .maybeSingle();

    if (data) {
      setLogData({
        supervisor_id: data.supervisor_id || "",
        labour_details: data.labour_details || [
          { type: "Skilled", count: 0 },
        ],
        materials: data.material_details || [],
        expenses: data.expense_details || [],
        opening_balance: data.opening_balance ?? prevClosing,
        closing_balance: data.closing_balance ?? 0,
      });

      setOpeningBalance(data.opening_balance ?? prevClosing);
      setClosingBalance(data.closing_balance ?? 0);
      setCashIn(data.cash_in || 0);
    } else {
      setLogData({
        supervisor_id: "",
        labour_details: [
          { type: "Skilled", count: 0 },
        ],
        materials: [],
        expenses: [],
        opening_balance: prevClosing,
        closing_balance: 0,
      });

      setOpeningBalance(prevClosing);
      setClosingBalance(prevClosing);
      setCashIn(0);
    }

    setLoading(false);
  }

  const handleStatusChange = async (newStatus) => {
    try {
      const { error } = await supabase
        .from("sites")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) throw error;
      setSite({ ...site, status: newStatus });
      toast.success(`Site status updated to ${newStatus}`);
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const materialTotal = logData.materials.reduce(
        (s, m) => s + Number(m.cost || 0),
        0,
      );

      const miscTotal = logData.expenses.reduce(
        (s, e) => s + Number(e.amount || 0),
        0,
      );

      const dailyTotal = materialTotal + miscTotal;

      const closingBalance = Number(logData.opening_balance) - dailyTotal;

      const uploadEntries = async (entries, type) => {
        const updated = [];

        for (const entry of entries) {
          let url = entry.url;

          if (entry.file) {
            const cleanName = entry.file.name.replace(/[^a-zA-Z0-9.]/g, "_");
            const path = `${id}/${type}/${Date.now()}_${cleanName}`;

            const { error } = await supabase.storage
              .from("site-docs")
              .upload(path, entry.file);

            if (!error) {
              const { data } = supabase.storage
                .from("site-docs")
                .getPublicUrl(path);

              url = data.publicUrl;
            }
          }

          updated.push({
            title: entry.title,
            amount: Number(entry.amount || 0),
            url,
          });
        }

        return updated;
      };

      const updatedMb = [
        ...mbEntries.filter((e) => e.url && !e.file),
        ...(await uploadEntries(mbEntries, "mb")),
      ];

      const updatedInvoice = [
        ...invoiceEntries.filter((e) => e.url && !e.file),
        ...(await uploadEntries(invoiceEntries, "invoice")),
      ];

      const { error } = await supabase.from("daily_logs").upsert(
        {
          site_id: id,
          date: selectedDate,
          supervisor_id: logData.supervisor_id || null,
          labour_count: logData.labour_details.reduce(
            (s, l) => s + Number(l.count),
            0,
          ),
          labour_details: logData.labour_details,
          material_cost: materialTotal,
          material_details: logData.materials,
          expense_details: logData.expenses,
          total_expenses: dailyTotal,
          opening_balance: logData.opening_balance,
          closing_balance: closingBalance,
          cash_in: cashIn,
        },
        { onConflict: "site_id,date" },
      );

      await supabase
        .from("sites")
        .update({
          mb_files: updatedMb,
          invoice_files: updatedInvoice,
        })
        .eq("id", id);

      if (error) throw error;
      toast.success("Operational logs updated");
      fetchInitialData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save logs");
    } finally {
      setSaving(false);
    }
  };

  const handleAddCash = () => {
    const amount = Number(addCash || 0);
    if (!amount) return;

    setCashIn((prev) => prev + amount);

    setLogData((prev) => ({
      ...prev,
      opening_balance: Number(prev.opening_balance || 0) + amount,
    }));

    setAddCash("");
  };

  const handleDeleteCash = async () => {
    const { error } = await supabase
      .from("daily_logs")
      .update({ cash_in: 0 })
      .eq("site_id", id)
      .eq("date", selectedDate);

    if (error) {
      toast.error("Failed to remove cash");
    } else {
      toast.success("Cash removed");
      setCashIn(0);

      setLogData((prev) => ({
        ...prev,
        opening_balance:
          Number(prev.opening_balance || 0) - Number(cashIn || 0),
      }));
    }
  };

  const totalMbAmount = mbEntries.reduce(
    (sum, m) => sum + Number(m.amount || 0),
    0,
  );

  const profitLoss = totalMbAmount - summary.totalSpend;

  const STATUS_COLORS = {
    active: "border-emerald-500 bg-emerald-50/30",
    construction: "border-orange-500 bg-orange-50/30",
    completed: "border-blue-500 bg-blue-50/30",
    MB_Running: "border-purple-500 bg-purple-50/30",
    MB_Completed: "border-indigo-500 bg-indigo-50/30",
    invoices: "border-pink-500 bg-pink-50/30",
    Payment_Received: "border-cyan-500 bg-cyan-50/30",
  };

const generatePDFReport = async () => {
    setLoading(true);
    const data = await getReportData();
    if (!data || data.length === 0) {
      toast.error("No data found for the selected period");
      setLoading(false);
      return;
    }

    const doc = new jsPDF();
    const isMultiDay = data.length > 1;

    // 1. FORMATTING HELPERS
    const formatDateFull = (dateStr) => {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    };

    // 1. HEADER
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(0, 84, 166);
    doc.text("SHREE CONSTRUCTION", 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    const title = reportConfig.type === 'full' ? "COMPLETE PROJECT AUDIT" : "SITE OPERATIONS REPORT";
    doc.text(`${title} | SITE: ${site?.site_name?.toUpperCase()}`, 14, 26);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 31);

    // 3. DAILY BALANCE & CASH FLOW TABLE (New Requirement)
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text("Daily Balance & Cash Inflow Tracking", 14, 45);

    autoTable(doc, {
      startY: 35,
      head: [["Date", "Opening Balance", "Cash Added (+)", "Daily Outflow (-)", "Closing Balance"]],
      body: data.map(d => [
        d.date,
        `Rs. ${Number(d.opening_balance || 0).toLocaleString()}`,
        `Rs. ${Number(d.cash_in || 0).toLocaleString()}`,
        `Rs. ${Number(d.total_expenses || 0).toLocaleString()}`,
        `Rs. ${Number(d.closing_balance || 0).toLocaleString()}`
      ]),
      headStyles: { fillColor: [15, 23, 42] },
    });

    let currentY = doc.lastAutoTable.finalY + 15;

    // 4. DETAILED DATE-WISE OPERATIONS (Materials & Expenses)
    // ... previous PDF code (Header & Balance Tracking Table) ...

    // 4. DETAILED DATE-WISE OPERATIONS
    doc.text("Date-wise Material & Spend Breakdown", 14, currentY);
    currentY += 5;

    const operationalRows = [];
    let cumulativeGrandTotal = 0;

    data.forEach((day) => {
      // Find Supervisor name
      const supervisor = supervisors.find(s => s.id === day.supervisor_id);
      const supervisorName = supervisor ? supervisor.name : "Not Assigned";

      // DATE & SUPERVISOR HEADER
      operationalRows.push([
        { 
          content: `DATE: ${formatDateFull(day.date)} | SUPERVISOR: ${supervisorName.toUpperCase()}`, 
          colSpan: 4, 
          styles: { fillColor: [226, 232, 240], fontStyle: 'bold', textColor: [15, 23, 42] } 
        }
      ]);

      // BALANCES ROW
      operationalRows.push([
        { 
          content: `Opening Balance: Rs. ${Number(day.opening_balance || 0).toLocaleString()}`, 
          colSpan: 2, 
          styles: { fillColor: [248, 250, 252], textColor: [22, 163, 74], fontStyle: 'bold', fontSize: 8 } 
        },
        { 
          content: `Closing Balance: Rs. ${Number(day.closing_balance || 0).toLocaleString()}`, 
          colSpan: 2, 
          styles: { fillColor: [248, 250, 252], textColor: [220, 38, 38], fontStyle: 'bold', fontSize: 8, halign: 'right' } 
        }
      ]);

      // Add Materials
      if (day.material_details?.length > 0) {
        day.material_details.forEach(m => {
          operationalRows.push([m.name, m.qty, m.unit, `Rs. ${Number(m.cost).toLocaleString()}`]);
        });
      }

      // Add Expenses
      if (day.expense_details?.length > 0) {
        day.expense_details.forEach(e => {
          operationalRows.push([`[Misc] ${e.item}`, '-', '-', `Rs. ${Number(e.amount).toLocaleString()}`]);
        });
      }

      // --- NEW: MB & INVOICE TRACKING ---
      // Check if any MB or Invoices were uploaded on this specific date
      // Note: This logic assumes your document titles or URLs contain a timestamp or specific date reference 
      // Or you can match them if you've added a 'created_at' field to the files.
      // If you don't store individual dates per file, this will show all site files.
      
      const dailyMB = mbEntries.filter(mb => mb.url && mb.url.includes(day.date));
      const dailyInvoices = invoiceEntries.filter(inv => inv.url && inv.url.includes(day.date));

      if (dailyMB.length > 0 || dailyInvoices.length > 0) {
        operationalRows.push([{ content: 'DOCUMENTS UPLOADED TODAY', colSpan: 4, styles: { fontStyle: 'bold', fontSize: 7, textColor: [0, 84, 166] } }]);
        dailyMB.forEach(m => operationalRows.push([`[MB File] ${m.title}`, '-', '-', `Rs. ${Number(m.amount).toLocaleString()}`]));
        dailyInvoices.forEach(i => operationalRows.push([`[Invoice] ${i.title}`, '-', '-', '-']));
      }

      // Daily Total Outflow
      operationalRows.push([
        { content: `Total Outflow`, colSpan: 3, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: `Rs. ${Number(day.total_expenses).toLocaleString()}`, styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } }
      ]);

      operationalRows.push([{ content: '', colSpan: 4, styles: { cellPadding: 1 } }]);
      cumulativeGrandTotal += Number(day.total_expenses || 0);
    });

    // CUMULATIVE PROJECT GRAND TOTAL
    operationalRows.push([
      { content: 'PROJECT CUMULATIVE GRAND TOTAL OUTFLOW', colSpan: 3, styles: { fillColor: [0, 84, 166], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'right' } },
      { content: `Rs. ${cumulativeGrandTotal.toLocaleString()}`, styles: { fillColor: [0, 84, 166], textColor: [255, 255, 255], fontStyle: 'bold' } }
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [["Item Description", "Qty", "Unit", "Total Cost"]],
      body: operationalRows,
      theme: 'grid',
      headStyles: { fillColor: [0, 84, 166] },
      styles: { fontSize: 8 },
      rowPageBreak: 'avoid',
    });

    // ... rest of the code (pseudo-graphs and page numbers) ...
    currentY = doc.lastAutoTable.finalY + 20;

    // 5. VISUAL FLOW SUMMARY (Pseudo-Graph Section)
    if (currentY > 230) { doc.addPage(); currentY = 20; }
    
    doc.setFontSize(14);
    doc.setTextColor(0, 84, 166);
    doc.text("Project Outflow Summary", 14, currentY);

    // Drawing a simple bar indicator for Material vs Misc
    const totalMat = data.reduce((s, d) => s + Number(d.material_cost || 0), 0);
    const totalMisc = cumulativeGrandTotal - totalMat;
    const matWidth = (totalMat / cumulativeGrandTotal) * 180;
    const miscWidth = (totalMisc / cumulativeGrandTotal) * 180;

    currentY += 10;
    doc.setFillColor(0, 84, 166); // Blue for Material
    doc.rect(14, currentY, matWidth, 8, "F");
    doc.setFillColor(234, 179, 8); // Yellow for Misc
    doc.rect(14 + matWidth, currentY, miscWidth, 8, "F");

    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Material: Rs. ${totalMat.toLocaleString()} (Blue)`, 14, currentY + 15);
    doc.text(`Misc Spends: Rs. ${totalMisc.toLocaleString()} (Yellow)`, 100, currentY + 15);

    // Final Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, 105, 290, { align: 'center' });
    }
doc.save(`Site_Report_${site?.site_name || 'Export'}_${new Date().toISOString().slice(0, 10)}.pdf`);
    setLoading(false);
    setReportModalOpen(false);
    // window.open(doc.output('bloburl'), '_blank');
  };

  if (!site)
    return <div className="p-20 text-center font-bold">Loading Site...</div>;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6 pb-24 animate-in fade-in duration-500">
      {/* 1. TOP NAVIGATION & SUMMARY SECTION */}
      <div className={`bg-white rounded-[2rem] p-6 border-t-4 shadow-sm space-y-6 transition-all duration-500 ${STATUS_COLORS[site.status] || 'border-slate-100'}`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/50 rounded-full transition-all"
            >
              <ArrowLeft size={20} className="text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                {site.site_name}
              </h1>
              <div className="flex items-center gap-2">
                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">
                  {site.location}
                </p>
                <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                <p className="text-[10px] font-black uppercase text-blue-600">
                  {site.status}
                </p>
              </div>
            </div>
          </div>

          {/* Date Selector with Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setReportModalOpen(true)}
              className="px-4 py-2 bg-slate-900 text-white rounded-2xl text-xs font-black hover:bg-slate-800 transition flex items-center gap-2 shadow-sm"
            >
              <FileText size={16} /> Generate PDF
            </button>

            <div className="flex items-center bg-white/80 backdrop-blur-sm p-1.5 rounded-2xl border border-slate-200 shadow-sm">
              <button
                onClick={() => handleDateStep(-1)}
                className="p-2 hover:bg-white rounded-xl transition-all text-slate-600"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="px-4 flex flex-col items-center min-w-[140px]">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">
                  Reporting Date
                </span>
                <div
                  className="relative text-center cursor-pointer"
                  onClick={() => document.getElementById("hiddenDateInput").showPicker()}
                >
                  <span className="font-black text-sm">
                    {new Date(selectedDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                  <input
                    id="hiddenDateInput"
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="absolute inset-0 opacity-0 pointer-events-none"
                  />
                </div>
              </div>
              <button
                onClick={() => handleDateStep(1)}
                className="p-2 hover:bg-white rounded-xl transition-all text-slate-600"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Total Summary Ribbon */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-50">
          <SummaryMiniCard
            label="Days"
            value={summary.totalDays}
            sub="Days worked"
          />
          <SummaryMiniCard
            label="Net Spend"
            value={`₹${summary.totalSpend.toLocaleString()}`}
            sub="Project Total"
            color="text-emerald-600"
          />
          {/* <SummaryMiniCard
            label="Site Status"
            value={site.status}
            sub="Current phase"
            color="text-blue-600"
          /> */}

          {/* Site Status Dropdown Card */}
          <div className="space-y-1">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">
              Site Status
            </p>
            <select
              value={site.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="block w-full bg-transparent border-none p-0 text-lg font-black tracking-tight text-blue-600 focus:ring-0 cursor-pointer hover:text-blue-700 transition-colors"
            >
              <option value="active">ACTIVE</option>



              <option value="construction">Under Construction</option>

              <option value="completed">Completed</option>

              <option value="MB_Running">MB Running</option>

              <option value="MB_Completed">MB Completed</option>

              <option value="invoices">Invoices</option>

              <option value="Payment_Received">Payment Received</option>
            </select>
            <p className="text-[9px] font-medium text-slate-400 uppercase">Current phase</p>
          </div>
          <SummaryMiniCard
            label="P/L"
            value={`₹${profitLoss.toLocaleString()}`}
            sub={profitLoss >= 0 ? "Profit" : "Loss"}
            color={profitLoss >= 0 ? "text-emerald-600" : "text-red-600"}
          />
          {Number(cashIn) > 0 && (
            <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-4">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
                  Cash Added
                </p>
                <p className="text-xl font-black text-emerald-600">
                  ₹{Number(cashIn || 0).toLocaleString()}
                </p>
              </div>
              <button
                onClick={handleDeleteCash}
                className="px-3 py-2 bg-red-50 text-red-600 text-xs font-black rounded-xl hover:bg-red-100 transition"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>



      <div className="flex gap-2">
        <input
          type="number"
          placeholder="Add cash..."
          value={addCash}
          onChange={(e) => setAddCash(e.target.value)}
          className="flex-1 bg-slate-100 rounded-xl px-3 py-2 text-sm font-bold focus:ring-1 focus:ring-blue-200"
        />

        <button
          onClick={handleAddCash}
          className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-xs font-black hover:bg-emerald-600 transition"
        >
          + Add
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="h-12 w-12 bg-blue-50 rounded-2xl flex items-center justify-center text-[#0054A6]">
            <Wallet size={24} />
          </div>
          <div className="flex-grow">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
              Opening Cash
            </p>
            <input
              type="number"
              className="text-xl font-black text-slate-900 border-none p-0 focus:ring-0 w-full"
              value={logData.opening_balance}
              onChange={(e) =>
                setLogData({
                  ...logData,
                  opening_balance: Number(e.target.value),
                })
              }
            />
          </div>
        </div>

        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="h-12 w-12 bg-red-50 rounded-2xl flex items-center justify-center text-red-500">
            <TrendingUp className="rotate-180" size={24} />
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
              Today's Outflow
            </p>
            <p className="text-xl font-black text-red-500">
              -₹{dailyOutflow.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="bg-slate-900 p-6 rounded-[2rem] shadow-xl flex items-center gap-4 text-white">
          <div className="h-12 w-12 bg-white/10 rounded-2xl flex items-center justify-center text-emerald-400">
            <IndianRupee size={24} />
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
              Closing Balance
            </p>
            <p className="text-xl font-black text-emerald-400">
              ₹{closingBalance.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* 2. LEFT COLUMN: PERSONNEL + DOCUMENTS */}
        <div className="lg:col-span-3 space-y-6">
          <div className="space-y-4">
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white p-6 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <HardHat size={16} className="text-orange-500" /> Personnel
                  Management
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 mb-2 block ml-1 uppercase">
                      Assigned Supervisor
                    </label>
                    <select
                      className="w-full bg-slate-50 border-none rounded-2xl font-bold text-sm h-12 px-4 focus:ring-2 focus:ring-[#0054A6]"
                      value={logData.supervisor_id}
                      onChange={(e) =>
                        setLogData({
                          ...logData,
                          supervisor_id: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Name</option>
                      {supervisors.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {logData.labour_details.map((labour, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-slate-50 p-4 rounded-2xl border border-slate-100"
                    >
                      <span className="font-bold text-slate-600 text-sm">
                        {labour.type} Labour
                      </span>
                      <div className="flex items-center gap-3 bg-white rounded-xl p-1 shadow-sm border border-slate-200">
                        <button
                          onClick={() => {
                            const newL = [...logData.labour_details];
                            newL[i].count = Math.max(0, newL[i].count - 1);
                            setLogData({ ...logData, labour_details: newL });
                          }}
                          className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="font-black text-base w-6 text-center">
                          {labour.count}
                        </span>
                        <button
                          onClick={() => {
                            const newL = [...logData.labour_details];
                            newL[i].count += 1;
                            setLogData({ ...logData, labour_details: newL });
                          }}
                          className="p-1.5 hover:bg-emerald-50 text-emerald-500 rounded-lg transition-colors"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <DocumentUploadSection
            title="MB Documents"
            entries={mbEntries}
            setEntries={setMbEntries}
            storageType="mb"
            id={id}
          />
          <DocumentUploadSection
            title="Invoice Documents"
            entries={invoiceEntries}
            setEntries={setInvoiceEntries}
            storageType="invoice"
            id={id}
          />
        </div>

        {/* 3. RIGHT COLUMN: MATERIALS & EXPENSES */}
        <div className="lg:col-span-9 space-y-6">
          {/* Materials Consumption Section */}
          <div className="bg-white p-6 rounded-[2.5rem] border border-slate-200 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Package size={16} className="text-[#0054A6]" /> Material
                Consumption
              </h3>
              <button
                onClick={() =>
                  setLogData({
                    ...logData,
                    materials: [
                      ...logData.materials,
                      { name: "", qty: "", unit: "Bags", cost: "" },
                    ],
                  })
                }
                className="text-[10px] font-black bg-blue-50 text-[#0054A6] px-4 py-2 rounded-xl hover:bg-[#0054A6] hover:text-white transition-all uppercase"
              >
                + Add Entry
              </button>
            </div>

            <div className="space-y-3">
              {logData.materials.map((m, i) => (
                <div
                  key={i}
                  className="flex flex-wrap md:flex-nowrap gap-2 bg-slate-50 p-3 rounded-2xl items-center border border-slate-100 group"
                >
                  <input
                    placeholder="Material description..."
                    className="w-full md:flex-1 bg-white border-none rounded-xl text-sm font-bold h-10 px-4 focus:ring-1 focus:ring-blue-200"
                    value={m.name}
                    onChange={(e) => {
                      const newM = [...logData.materials];
                      newM[i].name = e.target.value;
                      setLogData({ ...logData, materials: newM });
                    }}
                  />
                  <div className="flex items-center gap-1 w-full md:w-auto">
                    <input
                      type="number"
                      placeholder="Qty"
                      className="w-20 bg-white border-none rounded-xl text-sm font-black h-10 px-3 focus:ring-1 focus:ring-blue-200"
                      value={m.qty}
                      onChange={(e) => {
                        const newM = [...logData.materials];
                        newM[i].qty = e.target.value;
                        setLogData({ ...logData, materials: newM });
                      }}
                    />
                    <select
                      className="bg-white border-none rounded-xl text-[10px] font-black h-10 px-2 focus:ring-0"
                      value={m.unit}
                      onChange={(e) => {
                        const newM = [...logData.materials];
                        newM[i].unit = e.target.value;
                        setLogData({ ...logData, materials: newM });
                      }}
                    >
                      <option>Unit</option>
                      <option>Meter</option>
                      <option>Square Meter</option>
                      <option>Cubic Meter</option>
                      <option>kg</option>
                      <option>ton</option>
                      <option>bags</option>
                      <option>liters</option>
                      <option>sheets</option>
                    </select>
                  </div>

                  <div className="w-full md:w-44 flex items-center gap-2">
                    <div className="flex items-center bg-white rounded-xl border px-2 h-10 w-1/2">
                      <span className="text-slate-300 text-[10px] font-black mr-1">
                        ₹
                      </span>
                      <input
                        type="number"
                        placeholder="Per Unit"
                        className="w-full border-none text-sm font-black focus:ring-0 text-blue-600"
                        value={m.pricePerUnit || ""}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          const newM = [...logData.materials];
                          newM[i].pricePerUnit = value;
                          newM[i].cost = (value * (newM[i].qty || 0)).toFixed(2);
                          setLogData({ ...logData, materials: newM });
                        }}
                      />
                    </div>

                    <div className="flex items-center bg-white rounded-xl border px-2 h-10 w-1/2">
                      <span className="text-slate-300 text-[10px] font-black mr-1">
                        ₹
                      </span>
                      <input
                        type="number"
                        placeholder="Total"
                        className="w-full border-none text-sm font-black focus:ring-0 text-emerald-600"
                        value={m.cost || ""}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          const newM = [...logData.materials];
                          newM[i].cost = value;
                          if (newM[i].qty) {
                            newM[i].pricePerUnit = (value / newM[i].qty).toFixed(2);
                          }
                          setLogData({ ...logData, materials: newM });
                        }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      setLogData({
                        ...logData,
                        materials: logData.materials.filter(
                          (_, idx) => idx !== i,
                        ),
                      })
                    }
                    className="p-2 text-slate-300 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Miscellaneous Expenses Section */}
          <div className="bg-white p-4 md:p-6 rounded-[2.5rem] border border-slate-200 shadow-sm">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
              <Wallet size={16} className="text-emerald-500" /> Miscellaneous &
              Daily Spends
            </h3>

            <div className="space-y-3">
              {logData.expenses.map((exp, i) => (
                <div
                  key={i}
                  className="flex flex-col sm:flex-row gap-3 bg-slate-50 p-3 rounded-2xl border border-slate-100 items-stretch sm:items-center animate-in slide-in-from-bottom-2"
                >
                  <div className="flex-grow">
                    <input
                      placeholder="Expense detail..."
                      className="w-full bg-white border border-slate-100 rounded-xl text-sm font-bold h-11 px-4 focus:ring-2 focus:ring-blue-100 outline-none"
                      value={exp.item}
                      onChange={(e) => {
                        const newE = [...logData.expenses];
                        newE[i].item = e.target.value;
                        setLogData({ ...logData, expenses: newE });
                      }}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex-grow sm:w-32 flex items-center bg-white rounded-xl border border-slate-100 px-3 h-11 shadow-sm">
                      <span className="text-slate-300 text-xs font-black mr-2">
                        ₹
                      </span>
                      <input
                        type="number"
                        placeholder="0"
                        className="w-full border-none text-sm font-black focus:ring-0 text-[#0054A6] bg-transparent"
                        value={exp.amount}
                        onChange={(e) => {
                          const newE = [...logData.expenses];
                          newE[i].amount = e.target.value;
                          setLogData({ ...logData, expenses: newE });
                        }}
                      />
                    </div>
                    <button
                      onClick={() =>
                        setLogData({
                          ...logData,
                          expenses: logData.expenses.filter(
                            (_, idx) => idx !== i,
                          ),
                        })
                      }
                      className="p-2.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}

              <button
                onClick={() =>
                  setLogData({
                    ...logData,
                    expenses: [...logData.expenses, { item: "", amount: "" }],
                  })
                }
                className="w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 font-black text-[10px] uppercase hover:border-[#0054A6] hover:text-[#0054A6] hover:bg-blue-50 transition-all"
              >
                <Plus size={16} /> New Expense Row
              </button>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-50 flex flex-col md:flex-row justify-between items-center gap-8">
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className="h-14 w-14 bg-blue-50 rounded-2xl flex items-center justify-center text-[#0054A6] shrink-0">
                  <IndianRupee size={28} />
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-left">
                    Net Outflow (Today)
                  </p>
                  <p className="text-3xl md:text-4xl font-black text-slate-900 text-left">
                    ₹
                    {(
                      logData.materials.reduce(
                        (s, m) => s + Number(m.cost || 0),
                        0,
                      ) +
                      logData.expenses.reduce(
                        (s, e) => s + Number(e.amount || 0),
                        0,
                      )
                    ).toLocaleString()}
                  </p>
                </div>
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className={`w-full md:w-auto px-10 py-4 rounded-2xl font-black shadow-xl transition-all flex items-center justify-center gap-3 shrink-0 ${saving
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#0054A6] hover:bg-blue-700 active:scale-95"
                  }`}
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} /> Update Records
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- Report Selection Modal --- */}
      {reportModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <h2 className="text-2xl font-black text-slate-900 mb-2">Generate Report</h2>
            <p className="text-slate-500 text-sm mb-8 font-medium">Select the data range for the PDF export.</p>

            <div className="space-y-6">
              {/* Type Selector */}
              <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                {['single', 'range', 'full'].map((t) => (
                  <button
                    key={t}
                    onClick={() => setReportConfig({ ...reportConfig, type: t })}
                    className={`flex-1 py-2 text-[10px] font-black uppercase rounded-xl transition-all ${reportConfig.type === t ? 'bg-white text-[#0054A6] shadow-sm' : 'text-slate-400'
                      }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Date Inputs */}
              {reportConfig.type !== 'full' && (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase ml-2 mb-1 block">
                      {reportConfig.type === 'range' ? 'Start Date' : 'Select Date'}
                    </label>
                    <input
                      type="date"
                      value={reportConfig.startDate}
                      onChange={(e) => setReportConfig({ ...reportConfig, startDate: e.target.value })}
                      className="w-full bg-slate-50 border-none rounded-2xl font-bold p-4 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {reportConfig.type === 'range' && (
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase ml-2 mb-1 block">End Date</label>
                      <input
                        type="date"
                        value={reportConfig.endDate}
                        onChange={(e) => setReportConfig({ ...reportConfig, endDate: e.target.value })}
                        className="w-full bg-slate-50 border-none rounded-2xl font-bold p-4 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </div>
              )}

              {reportConfig.type === 'full' && (
                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
                  <p className="text-blue-700 text-xs font-bold text-center">
                    This will aggregate all historical logs for this site into one document.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setReportModalOpen(false)}
                  className="flex-1 py-4 text-slate-500 font-black text-xs uppercase hover:bg-slate-50 rounded-2xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={generatePDFReport}
                  className="flex-[2] py-4 bg-[#0054A6] text-white font-black text-xs uppercase rounded-2xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentUploadSection({
  title,
  entries,
  setEntries,
  storageType,
  id,
}) {
  return (
    <div className="bg-white p-6 rounded-[2.5rem] border shadow-sm space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">
          {title}
        </h3>
        <button
          onClick={() =>
            setEntries([...entries, { title: "", file: null, url: "" }])
          }
          className="text-[9px] font-black bg-slate-100 px-3 py-1.5 rounded-lg"
        >
          + ADD
        </button>
      </div>
      <div className="space-y-3">
        {entries.map((entry, i) => (
          <div
            key={i}
            className="space-y-2 p-3 bg-slate-50 rounded-2xl border border-slate-100"
          >
            <input
              placeholder="Doc Title..."
              className="w-full bg-white border-none rounded-xl px-3 py-2 text-xs font-bold"
              value={entry.title}
              onChange={(e) => {
                const n = [...entries];
                n[i].title = e.target.value;
                setEntries(n);
              }}
            />

            <input
              type="number"
              placeholder="Amount ₹"
              className="w-full bg-white border-none rounded-xl px-3 py-2 text-xs font-bold"
              value={entry.amount || ""}
              onChange={(e) => {
                const n = [...entries];
                n[i].amount = e.target.value;
                setEntries(n);
              }}
            />
            <div className="flex items-center justify-between gap-2">
              <div className="relative flex-1">
                <input
                  type="file"
                  className="absolute inset-0 opacity-0 cursor-pointer w-full"
                  onChange={(e) => {
                    const n = [...entries];
                    n[i].file = e.target.files[0];
                    setEntries(n);
                  }}
                />
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl text-[10px] font-bold text-slate-500 border border-slate-100">
                  <FileUp size={14} />{" "}
                  {entry.file ? entry.file.name.slice(0, 15) : "Upload File"}
                </div>
              </div>
              <div className="flex gap-1">
                {entry.url && (
                  <a
                    href={entry.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                  >
                    <Eye size={14} />
                  </a>
                )}
                <button
                  onClick={() =>
                    setEntries(entries.filter((_, idx) => idx !== i))
                  }
                  className="p-2 bg-red-50 text-red-500 rounded-lg hover:bg-red-100"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryMiniCard({ label, value, sub, color = "text-slate-900" }) {
  return (
    <div className="space-y-1">
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">
        {label}
      </p>
      <p className={`text-lg font-black tracking-tight ${color}`}>{value}</p>
      <p className="text-[9px] font-medium text-slate-400 uppercase">{sub}</p>
    </div>
  );
}